import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarBlank,
  Trophy,
  UserCircle,
  Users,
  Pencil,
  Plus,
  TrashSimple,
  WarningCircle,
  Clock,
  MapPin,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { api, formatErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

const PLACEHOLDER_COVER =
  "linear-gradient(120deg,#0a0a0a 0%,#111 40%,#1a1a1a 100%)";
const PLACEHOLDER_PORTRAIT =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' fill='%23111'/><circle cx='32' cy='24' r='10' fill='%23333'/><path d='M8 60c4-14 16-20 24-20s20 6 24 20' fill='%23333'/></svg>";

export default function TeamDetail() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEdit =
    user &&
    (user.role === "admin" || (user.role === "trainer" && user.team_id === teamId));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/${teamId}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0A0A0A] text-neutral-400">
        Loader hold…
      </div>
    );
  if (!data)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0A0A0A] text-neutral-400">
        Hold findes ikke.{" "}
        <Link className="text-brand underline" to="/">
          Tilbage
        </Link>
      </div>
    );

  const {
    team,
    profile,
    training,
    overrides,
    manual_players,
    dbu_players,
    news,
    matches,
  } = data;

  const upcoming = matches.filter((m) => m.type !== "resultat");
  const results = matches.filter((m) => m.type === "resultat");
  const futureOverrides = overrides
    .filter((o) => new Date(o.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => a.date.localeCompare(b.date));

  const icalUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ical/team/${teamId}.ics`;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="team-detail-page"
    >
      {/* Cover */}
      <section className="relative isolate h-[60vh] min-h-[420px] overflow-hidden">
        {profile.cover_image ? (
          <img
            src={profile.cover_image}
            alt={`${team.name} coverbillede`}
            className="absolute inset-0 h-full w-full object-cover opacity-70"
          />
        ) : (
          <div
            className="absolute inset-0 pitch-lines"
            style={{ background: PLACEHOLDER_COVER }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-[#0A0A0A]" />

        <Link
          to="/#hold"
          data-testid="team-back-link"
          className="mono-label absolute left-6 top-6 inline-flex items-center gap-2 text-neutral-300 hover:text-brand"
        >
          <ArrowLeft size={14} /> Alle hold
        </Link>

        <div className="absolute bottom-0 left-0 right-0 px-6 pb-10 lg:px-10 lg:pb-16">
          <div className="mx-auto max-w-7xl">
            <span className="mono-label text-brand">{team.league}</span>
            <h1
              data-testid="team-title"
              className="mt-3 font-heading text-6xl uppercase leading-none tracking-tight text-white sm:text-7xl lg:text-8xl"
            >
              {team.name}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 overflow-hidden border border-neutral-800 bg-black">
                  <img
                    src={profile.portrait_image || PLACEHOLDER_PORTRAIT}
                    alt={`Portræt af ${team.coach}`}
                    className="h-full w-full object-cover grayscale"
                  />
                </div>
                <div>
                  <div className="mono-label text-neutral-400">Cheftræner</div>
                  <div className="font-body text-lg text-white">{team.coach}</div>
                </div>
              </div>
              <div>
                <div className="mono-label text-neutral-400">Assistent</div>
                <div className="font-body text-lg text-white">
                  {team.assistant || "—"}
                </div>
              </div>
              <div>
                <div className="mono-label text-neutral-400">Trup</div>
                <div className="font-body text-lg text-white">
                  {team.squad_size || 0} spillere
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        {/* About + iCal subscribe */}
        <section className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <span className="section-eyebrow">Om holdet</span>
            <p
              data-testid="team-about"
              className="mt-4 max-w-2xl font-body text-lg leading-relaxed text-neutral-300"
            >
              {profile.about ||
                `Velkommen til ${team.name}. Vi træner ${training.length} gange om ugen og er en del af Taastrup FC's stærke fællesskab.`}
            </p>
          </div>
          <aside className="hairline bg-neutral-950 p-6">
            <span className="mono-label text-brand">Abonnér</span>
            <h3 className="mt-2 font-heading text-2xl uppercase tracking-tight">
              Føj til kalender
            </h3>
            <p className="mt-3 font-body text-sm text-neutral-400">
              Få automatisk alle træninger, kampe og opdateringer i Google /
              Apple Kalender via iCal-feedet.
            </p>
            <a
              href={icalUrl}
              data-testid="team-ical-download"
              className="btn-primary mt-5 text-xs"
            >
              <CalendarBlank size={14} /> Download .ics
            </a>
            <p className="mono-label mt-3 break-all text-neutral-500">{icalUrl}</p>
          </aside>
        </section>

        {/* Cancellations / extras alert */}
        {futureOverrides.length > 0 && (
          <section
            data-testid="team-overrides"
            className="mt-12 border-l-4 border-yellow-500 bg-yellow-500/5 p-5"
          >
            <div className="flex items-center gap-2 text-yellow-400">
              <WarningCircle size={18} weight="fill" />
              <span className="mono-label">Trænings-update</span>
            </div>
            <ul className="mt-3 space-y-2">
              {futureOverrides.map((o) => (
                <li
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-yellow-500/10 pb-2 last:border-0"
                  data-testid={`override-${o.id}`}
                >
                  <div>
                    <span
                      className={`mono-label mr-3 px-2 py-1 ${
                        o.kind === "cancellation"
                          ? "bg-red-500/20 text-red-300"
                          : "bg-brand/20 text-brand"
                      }`}
                    >
                      {o.kind === "cancellation" ? "Aflyst" : "Ekstra"}
                    </span>
                    <span className="font-body text-white">{o.date}</span>
                    {o.time && (
                      <span className="ml-3 font-mono text-sm text-neutral-400">
                        {o.time}
                      </span>
                    )}
                    {o.pitch && (
                      <span className="ml-3 font-mono text-sm text-neutral-400">
                        {o.pitch}
                      </span>
                    )}
                    {o.note && (
                      <span className="ml-3 font-body text-sm text-neutral-400">
                        — {o.note}
                      </span>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Fjern denne override?")) return;
                        await api.delete(
                          `/admin/team/${teamId}/training-override/${o.id}`
                        );
                        toast.success("Slettet");
                        reload();
                      }}
                      data-testid={`override-delete-${o.id}`}
                      className="mono-label inline-flex items-center gap-1 border border-yellow-500/30 px-2 py-1 text-yellow-300 hover:border-yellow-400"
                    >
                      <TrashSimple size={12} /> Slet
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Training + overrides editor */}
        <Section title="Træningstider" eyebrow="Ugeplan">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {training.map((t) => (
              <div
                key={t.id}
                data-testid={`team-training-${t.id}`}
                className="hairline bg-neutral-950 p-4"
              >
                <div className="mono-label text-brand">{t.day}</div>
                <div className="mt-2 flex items-center gap-2 font-mono text-lg text-white">
                  <Clock size={16} className="text-brand" /> {t.time}
                </div>
                <div className="mt-2 flex items-center gap-2 font-body text-sm text-neutral-300">
                  <MapPin size={14} /> {t.pitch}
                </div>
                <div className="mono-label mt-1 text-neutral-500">
                  {t.coach}
                </div>
              </div>
            ))}
            {training.length === 0 && (
              <p className="text-sm text-neutral-500">
                Ingen faste træninger registreret endnu.
              </p>
            )}
          </div>
          {canEdit && (
            <OverrideForm
              teamId={teamId}
              onCreated={reload}
            />
          )}
        </Section>

        {/* Matches */}
        {(upcoming.length > 0 || results.length > 0) && (
          <Section title="Kampe" eyebrow="Program">
            <div className="grid gap-4 sm:grid-cols-2">
              {upcoming.map((m) => (
                <MatchPill key={m.id} m={m} />
              ))}
              {results.map((m) => (
                <MatchPill key={m.id} m={m} />
              ))}
            </div>
          </Section>
        )}

        {/* Players */}
        <Section
          title="Spillere"
          eyebrow={dbu_players.length ? "Live fra DBU + manuelt" : "Trup"}
        >
          {dbu_players.length > 0 && (
            <div className="mb-6 overflow-x-auto border border-neutral-800">
              <table className="w-full text-left text-sm">
                <thead className="bg-black">
                  <tr className="mono-label text-neutral-500">
                    <th className="px-4 py-3">Spiller</th>
                    <th className="px-4 py-3">Kampe</th>
                    <th className="px-4 py-3">Mål</th>
                    <th className="px-4 py-3">Assists</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-900">
                  {dbu_players.map((p, i) => (
                    <tr key={`dbu-${i}`} data-testid={`dbu-player-${i}`}>
                      <td className="px-4 py-3 font-body text-white">{p.name}</td>
                      <td className="px-4 py-3 font-mono">{p.matches}</td>
                      <td className="px-4 py-3 font-mono text-brand">{p.goals}</td>
                      <td className="px-4 py-3 font-mono">{p.assists}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {manual_players.map((p) => (
              <div
                key={p.id}
                data-testid={`team-player-${p.id}`}
                className="hairline bg-neutral-950 p-4"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={p.image || PLACEHOLDER_PORTRAIT}
                    alt={p.name}
                    className="h-12 w-12 grayscale border border-neutral-800 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-body text-white">{p.name}</div>
                    <div className="mono-label text-neutral-500">
                      {p.position || "—"}
                    </div>
                  </div>
                  {p.number != null && (
                    <span className="font-heading text-2xl text-brand">
                      #{p.number}
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Slet denne spiller?")) return;
                        await api.delete(
                          `/admin/team/${teamId}/players/${p.id}`
                        );
                        toast.success("Slettet");
                        reload();
                      }}
                      data-testid={`team-player-delete-${p.id}`}
                      className="mono-label inline-flex items-center gap-1 border border-neutral-700 px-2 py-1 text-neutral-400 hover:border-red-500 hover:text-red-300"
                    >
                      <TrashSimple size={12} /> Slet
                    </button>
                  </div>
                )}
              </div>
            ))}
            {manual_players.length === 0 && dbu_players.length === 0 && (
              <p className="text-sm text-neutral-500">
                Ingen spillere registreret endnu.
              </p>
            )}
          </div>
          {canEdit && (
            <PlayerForm teamId={teamId} onCreated={reload} />
          )}
        </Section>

        {/* News */}
        <Section title="Holdnyt" eyebrow="Lige nu">
          <div className="grid gap-4 lg:grid-cols-2">
            {news.map((n) => (
              <article
                key={n.id}
                data-testid={`team-news-${n.id}`}
                className="hairline bg-neutral-950 p-6"
              >
                <span className="mono-label text-brand">{n.date}</span>
                <h3 className="mt-2 font-heading text-2xl uppercase tracking-tight text-white">
                  {n.title}
                </h3>
                <p className="mt-3 whitespace-pre-line font-body text-sm text-neutral-300">
                  {n.body}
                </p>
                {canEdit && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Slet denne nyhed?")) return;
                        await api.delete(
                          `/admin/team/${teamId}/news/${n.id}`
                        );
                        toast.success("Slettet");
                        reload();
                      }}
                      data-testid={`team-news-delete-${n.id}`}
                      className="mono-label inline-flex items-center gap-1 border border-neutral-700 px-2 py-1 text-neutral-400 hover:border-red-500 hover:text-red-300"
                    >
                      <TrashSimple size={12} /> Slet
                    </button>
                  </div>
                )}
              </article>
            ))}
            {news.length === 0 && (
              <p className="text-sm text-neutral-500">
                Ingen holdnyheder endnu.
              </p>
            )}
          </div>
          {canEdit && <NewsForm teamId={teamId} onCreated={reload} />}
        </Section>

        {/* Profile editor (admin/trainer) */}
        {canEdit && (
          <Section title="Holdprofil" eyebrow="Redigér">
            <ProfileForm
              teamId={teamId}
              initial={profile}
              onSaved={reload}
            />
          </Section>
        )}
      </main>
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <section className="mt-16">
      <div className="flex items-baseline justify-between">
        <div>
          <span className="section-eyebrow">{eyebrow}</span>
          <h2 className="mt-3 font-heading text-4xl uppercase tracking-tight sm:text-5xl">
            {title}
          </h2>
        </div>
      </div>
      <div className="mt-8">{children}</div>
    </section>
  );
}

function MatchPill({ m }) {
  const isResult = m.type === "resultat";
  return (
    <div className="hairline flex items-center justify-between bg-neutral-950 p-4">
      <div className="mono-label text-brand">{m.date} · {m.time}</div>
      <div className="font-body text-sm text-white text-center flex-1 mx-3">
        {m.home} <span className="text-neutral-500">vs</span> {m.away}
      </div>
      <div className="font-heading text-lg text-white">
        {isResult ? `${m.result_home}-${m.result_away}` : "—"}
      </div>
    </div>
  );
}

// -- Editor forms -----------------------------------------------------------

function ProfileForm({ teamId, initial, onSaved }) {
  const [form, setForm] = useState({
    about: initial.about || "",
    cover_image: initial.cover_image || "",
    portrait_image: initial.portrait_image || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/team/${teamId}/profile`, form);
      toast.success("Holdprofil opdateret");
      onSaved();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fejl");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="team-profile-form"
      className="hairline bg-neutral-950 p-6"
    >
      <Field
        label="Coverbillede URL"
        icon={<ImageIcon size={14} />}
        testid="profile-cover"
        value={form.cover_image}
        onChange={(v) => setForm({ ...form, cover_image: v })}
        placeholder="https://…"
      />
      <Field
        label="Portrætbillede URL (holdbillede)"
        icon={<ImageIcon size={14} />}
        testid="profile-portrait"
        value={form.portrait_image}
        onChange={(v) => setForm({ ...form, portrait_image: v })}
        placeholder="https://…  (sort/hvid portræt anbefales)"
      />
      <Field
        label="Om holdet"
        testid="profile-about"
        value={form.about}
        onChange={(v) => setForm({ ...form, about: v })}
        as="textarea"
      />
      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="profile-save"
          className="btn-primary text-xs disabled:opacity-60"
        >
          {saving ? "Gemmer…" : "Gem holdprofil"}
        </button>
      </div>
    </form>
  );
}

function NewsForm({ teamId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", date: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/team/${teamId}/news`, form);
      toast.success("Nyhed oprettet");
      setForm({ title: "", body: "", date: "" });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error("Fejl ved oprettelse");
    }
  };
  return open ? (
    <form onSubmit={submit} className="mt-6 hairline bg-neutral-950 p-6" data-testid="team-news-form">
      <Field label="Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="news-title" />
      <Field label="Dato" value={form.date} onChange={(v) => setForm({ ...form, date: v })} placeholder="12. maj 2026" required testid="news-date" />
      <Field label="Tekst" as="textarea" value={form.body} onChange={(v) => setForm({ ...form, body: v })} required testid="news-body" />
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs">Annuller</button>
        <button type="submit" className="btn-primary text-xs" data-testid="news-save">Gem nyhed</button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid="team-news-add"
      className="btn-ghost mt-6 text-xs"
    >
      <Plus size={14} /> Ny holdnyhed
    </button>
  );
}

function PlayerForm({ teamId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", number: "", position: "", image: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, number: form.number === "" ? null : Number(form.number) };
      await api.post(`/admin/team/${teamId}/players`, payload);
      toast.success("Spiller tilføjet");
      setForm({ name: "", number: "", position: "", image: "" });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error("Fejl ved oprettelse");
    }
  };
  return open ? (
    <form onSubmit={submit} className="mt-6 hairline bg-neutral-950 p-6 grid gap-4 sm:grid-cols-2" data-testid="team-player-form">
      <Field label="Navn" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="player-name" />
      <Field label="Nummer" type="number" value={form.number} onChange={(v) => setForm({ ...form, number: v })} testid="player-number" />
      <Field label="Position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} placeholder="Forsvar / Angreb / Målmand" testid="player-position" />
      <Field label="Billede URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="player-image" />
      <div className="sm:col-span-2 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs">Annuller</button>
        <button type="submit" className="btn-primary text-xs" data-testid="player-save">Tilføj spiller</button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid="team-player-add"
      className="btn-ghost mt-6 text-xs"
    >
      <Plus size={14} /> Tilføj spiller
    </button>
  );
}

function OverrideForm({ teamId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "cancellation", date: "", time: "", pitch: "", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/team/${teamId}/training-override`, form);
      toast.success(form.kind === "cancellation" ? "Træning aflyst" : "Ekstra træning oprettet");
      setForm({ kind: "cancellation", date: "", time: "", pitch: "", note: "" });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error("Fejl");
    }
  };
  return open ? (
    <form onSubmit={submit} className="mt-6 hairline bg-neutral-950 p-6 grid gap-4 sm:grid-cols-2" data-testid="team-override-form">
      <div>
        <label className="mono-label text-neutral-400">Type</label>
        <select
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          data-testid="override-kind"
          className="mt-2 w-full border border-neutral-800 bg-black px-3 py-2 text-white outline-none focus:border-brand"
        >
          <option value="cancellation">Aflys træning</option>
          <option value="extra">Ekstra træning</option>
        </select>
      </div>
      <Field label="Dato (åååå-mm-dd)" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required placeholder="2026-06-15" testid="override-date" />
      {form.kind === "extra" && (
        <>
          <Field label="Tid" value={form.time} onChange={(v) => setForm({ ...form, time: v })} placeholder="19:00 – 20:30" required testid="override-time" />
          <Field label="Bane" value={form.pitch} onChange={(v) => setForm({ ...form, pitch: v })} placeholder="Bane 3" testid="override-pitch" />
        </>
      )}
      <Field label="Note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} placeholder="(valgfri)" testid="override-note" />
      <div className="sm:col-span-2 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs">Annuller</button>
        <button type="submit" className="btn-primary text-xs" data-testid="override-save">Gem</button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid="team-override-add"
      className="btn-ghost mt-6 text-xs"
    >
      <Plus size={14} /> Aflys eller opret ekstra træning
    </button>
  );
}

function Field({ label, value, onChange, type = "text", as, required, placeholder, testid, icon }) {
  return (
    <div className="mt-4">
      <label className="mono-label inline-flex items-center gap-1 text-neutral-400">
        {icon} {label}
      </label>
      {as === "textarea" ? (
        <textarea
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          data-testid={testid}
          className="mt-2 w-full resize-none border border-neutral-800 bg-black px-3 py-2 text-white outline-none focus:border-brand"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          data-testid={testid}
          className="mt-2 w-full border border-neutral-800 bg-black px-3 py-2 text-white outline-none focus:border-brand"
        />
      )}
    </div>
  );
}
